import { post2, put2 } from "@/shared/axios";
import { errorToast, getFormData } from "@/shared/utils/helpers";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { isAiImage } from "../imageLibrary/aiImage";
import { MenuItemImageType } from "../utils/interfaces";
import useBranchId from "./useBranchId";

const useCloudinaryUploadHook = () => {
  const branch = useBranchId();

  const { t } = useTranslation();
  const updateCloudinaryInfo = useMutation({
    mutationFn: ({
      fileId,
      type,
    }: {
      fileId: string;
      type: MenuItemImageType;
    }) => {
      return put2(
        `internal/secure/pos/v1/menus/menu-media/${type}/${branch}/${fileId}`,
        {}
      );
    },
  });

  const uploadImageToCloudinary = useMutation({
    mutationFn: ({ url, formData }: any) => {
      return axios
        .post(url, formData)
        .then((res) => {
          toast.success(t("success.success"));
          return res;
        })
        .catch((err) => {
          errorToast();
          return err;
        });
    },
  });

  const getCloudinaryInfo = useMutation({
    mutationFn: ({ type }: { type: MenuItemImageType }) => {
      return post2(
        `/internal/secure/pos/v1/menus/menu-media/${type}/${branch}`
      );
    },
  });

  const uploadImage = (
    type: MenuItemImageType,
    image: File,
    fileId?: string,
    secureUrlOnly?: boolean
  ) => {
    return new Promise<string>(async (resolve, reject) => {
      try {
        let signData;
        // AI images belong to the image library, so an own upload gets a new file id.
        if (fileId && !isAiImage(fileId)) {
          signData = await updateCloudinaryInfo.mutateAsync({
            type,
            fileId,
          });
        } else {
          signData = await getCloudinaryInfo.mutateAsync({
            type,
          });
        }
        const formData = getFormData(image, signData);
        const { data } = await uploadImageToCloudinary.mutateAsync({
          url: signData.upload_url,
          formData,
        });
        const cloudinaryId = secureUrlOnly
          ? data?.secure_url?.split("/")?.reverse()?.[0]
          : `${data.version}/${data?.secure_url?.split("/")?.reverse()?.[0]}`;
        resolve(cloudinaryId);
      } catch {
        reject(undefined);
      }
    });
  };
  return {
    uploadImage,
    getCloudinaryInfo,
    updateCloudinaryInfo,
    uploadImageToCloudinary,
  };
};

export default useCloudinaryUploadHook;
